import React from "react"
import styled from "styled-components"
import { ArrowLeft, Send } from "lucide-react"
import { router } from "@inertiajs/react"

import { apiGet, apiPost } from "../../../api"
import { LoggedInUserContext } from "../../../providers/ProvideLoggedInUser"
import LeftSidebar from "../../components/left-sidebar"
import { AppLayout, FeedColumn, RightSidebar } from "../../constants/styles"
import TrendingPanel from "../../components/trending-panel"

const Header = styled.div`
  padding: 1rem; border-bottom: 1px solid var(--border);
  display: flex; align-items: center; gap: 0.75rem;
  h2 { font-size: 1.125rem; font-weight: 700; }
`
const BackBtn = styled.button`background: none; border: none; cursor: pointer; display: flex;`
const ChatArea = styled.div`
  flex: 1; overflow-y: auto; padding: 1rem; display: flex; flex-direction: column; gap: 0.5rem;
  min-height: 400px; max-height: 60vh;
`
const Bubble = styled.div`
  max-width: 75%; padding: 0.625rem 0.875rem; border-radius: 18px; font-size: 0.875rem;
  line-height: 1.4; word-wrap: break-word;
  align-self: ${(p) => p.$own ? "flex-end" : "flex-start"};
  background: ${(p) => p.$own ? "var(--primary, #ff9900)" : "var(--secondary, #f0f0f0)"};
  color: ${(p) => p.$own ? "#fff" : "var(--foreground)"};
`
const BubbleTime = styled.div`
  font-size: 0.6875rem; color: var(--muted-foreground); margin-top: 0.125rem;
  text-align: ${(p) => p.$own ? "right" : "left"};
`
const TypingIndicator = styled.div`
  font-size: 0.75rem; color: var(--muted-foreground); font-style: italic;
  padding: 0.25rem 0; min-height: 1.25rem;
`
const InputArea = styled.div`
  padding: 0.75rem 1rem; border-top: 1px solid var(--border);
  display: flex; align-items: center; gap: 0.5rem;
`
const MsgInput = styled.input`
  flex: 1; padding: 0.625rem 1rem; border: 1px solid var(--border); border-radius: 20px;
  font-size: 0.875rem; outline: none;
  &:focus { border-color: var(--primary); }
`
const SendBtn = styled.button`
  background: var(--primary, #ff9900); border: none; border-radius: 50%; width: 36px; height: 36px;
  display: flex; align-items: center; justify-content: center; cursor: pointer; color: #fff;
  &:disabled { opacity: 0.5; cursor: default; }
`
const Empty = styled.div`
  text-align: center; padding: 3rem; color: var(--muted-foreground);
`

const POLL_INTERVAL = 5000

export default function ConversationView({ conversationId }) {
  const { currentUser } = React.use(LoggedInUserContext)
  const [messages, setMessages] = React.useState([])
  const [otherUser, setOtherUser] = React.useState(null)
  const [input, setInput] = React.useState("")
  const [loading, setLoading] = React.useState(true)
  const [sending, setSending] = React.useState(false)
  const [otherTyping, setOtherTyping] = React.useState(false)
  const chatRef = React.useRef(null)
  const lastMessageTimeRef = React.useRef(null)
  const typingTimerRef = React.useRef(null)

  React.useEffect(() => {
    if (!conversationId) return
    apiGet(`/api/v1/communities/conversations/${conversationId}?perpage=100`).then((res) => {
      if (res.parsedResponse) {
        const msgs = res.parsedResponse.messages || []
        setMessages(msgs)
        setOtherUser(res.parsedResponse.conversation?.other_participant)
        if (msgs.length > 0) {
          lastMessageTimeRef.current = msgs[msgs.length - 1].created_at
        }
      }
    }).finally(() => setLoading(false))
  }, [conversationId])

  React.useEffect(() => {
    if (!conversationId || loading) return

    const poll = setInterval(() => {
      const after = lastMessageTimeRef.current || new Date(Date.now() - 60000).toISOString()
      apiGet(`/api/v1/communities/conversations/${conversationId}/poll_messages?after=${encodeURIComponent(after)}`).then((res) => {
        if (res.parsedResponse) {
          const newMsgs = res.parsedResponse.messages || []
          if (newMsgs.length > 0) {
            setMessages((prev) => {
              const existingIds = new Set(prev.map((m) => m.id))
              const fresh = newMsgs.filter((m) => !existingIds.has(m.id))
              if (fresh.length === 0) return prev
              lastMessageTimeRef.current = fresh[fresh.length - 1].created_at
              return [...prev, ...fresh]
            })
          }
          setOtherTyping(res.parsedResponse.other_is_typing || false)
        }
      })
    }, POLL_INTERVAL)

    return () => clearInterval(poll)
  }, [conversationId, loading])

  React.useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight
  }, [messages])

  const notifyTyping = () => {
    if (typingTimerRef.current) return
    apiPost(`/api/v1/communities/conversations/${conversationId}/typing`)
    typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null }, 3000)
  }

  const handleInputChange = (e) => {
    setInput(e.target.value)
    if (e.target.value.trim()) notifyTyping()
  }

  const sendMessage = async () => {
    if (!input.trim() || sending) return
    setSending(true)
    const res = await apiPost(`/api/v1/communities/conversations/${conversationId}/messages`, {
      body: { message: { content: input.trim() } }
    })
    if (res.parsedResponse?.data) {
      const newMsg = res.parsedResponse.data
      setMessages((prev) => [...prev, newMsg])
      lastMessageTimeRef.current = newMsg.created_at
      setInput("")
    }
    setSending(false)
  }

  return (
    <AppLayout>
      <LeftSidebar activePage="messages" />
      <FeedColumn>
        <Header>
          <BackBtn onClick={() => router.visit("/communities/messages")}><ArrowLeft size={20} /></BackBtn>
          <h2>{otherUser?.name || "Conversation"}</h2>
        </Header>
        <ChatArea ref={chatRef}>
          {loading ? <Empty>Loading...</Empty> : messages.length === 0 ? (
            <Empty>No messages yet. Say hello!</Empty>
          ) : messages.map((m) => (
            <div key={m.id}>
              <Bubble $own={m.is_own}>
                {m.is_deleted ? <em style={{opacity:0.6}}>This message was deleted</em> : m.content}
              </Bubble>
              <BubbleTime $own={m.is_own}>{new Date(m.created_at).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}</BubbleTime>
            </div>
          ))}
          <TypingIndicator>
            {otherTyping ? `${otherUser?.name || "User"} is typing...` : ""}
          </TypingIndicator>
        </ChatArea>
        <InputArea>
          <MsgInput value={input} onChange={handleInputChange} onKeyDown={(e) => { if (e.key === "Enter") sendMessage() }} placeholder="Type a message..." />
          <SendBtn onClick={sendMessage} disabled={!input.trim() || sending}><Send size={18} /></SendBtn>
        </InputArea>
      </FeedColumn>
      <RightSidebar><TrendingPanel /></RightSidebar>
    </AppLayout>
  )
}
